import React, { useState, useEffect } from 'react';
import {
    getFaqs,
    createFaq,
    updateFaq,
    deleteFaq,
    getFaqCategories,
    Faq,
    FaqCategory
} from '../api/admin';
import client from '../api/client';
import styles from './CommonPage.module.css';
import { Plus, Edit, Trash2, Eye, EyeOff, ChevronLeft, ChevronRight, Settings } from 'lucide-react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
// @ts-ignore
import ImageResize from 'quill-image-resize-module-react';
import FaqCategoryManager from '../components/FaqCategoryManager';

Quill.register('modules/imageResize', ImageResize);

export default function FaqPage() {
    const [faqs, setFaqs] = useState<Faq[]>([]);
    const [categories, setCategories] = useState<FaqCategory[]>([]);
    const [isCategoryManagerOpen, setIsCategoryManagerOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [formData, setFormData] = useState<{
        category: FaqCategory | null;
        question: string;
        answer: string;
        isVisible: boolean;
    }>({
        category: null,
        question: '',
        answer: '',
        isVisible: true
    });

    useEffect(() => {
        fetchFaqs();
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const data = await getFaqCategories();
            setCategories(data);
        } catch (error) {
            console.error('Failed to fetch categories');
        }
    };

    const fetchFaqs = async (pageIdx: number = 0) => {
        try {
            const data = await getFaqs(pageIdx, 10);
            setFaqs(data.content);
            setTotalPages(data.totalPages);
            setPage(data.number);
        } catch (error) {
            console.error('Failed to fetch FAQs', error);
        }
    };

    const handleCreate = () => {
        if (categories.length === 0) {
            alert('먼저 카테고리를 생성해주세요.');
            setIsCategoryManagerOpen(true);
            return;
        }
        setEditingId(null);
        setFormData({ category: categories[0], question: '', answer: '', isVisible: true });
        setIsModalOpen(true);
    };

    const handleEdit = (faq: Faq) => {
        setEditingId(faq.id);
        setFormData({
            category: faq.category,
            question: faq.question,
            answer: faq.answer,
            isVisible: faq.isVisible
        });
        setIsModalOpen(true);
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('정말 삭제하시겠습니까?')) return;
        try {
            await deleteFaq(id);
            fetchFaqs();
        } catch (error) {
            console.error('Failed to delete', error);
            alert('삭제에 실패했습니다.');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.question || !formData.answer || !formData.category) {
            alert('모든 필드를 입력해주세요.');
            return;
        }

        try {
            const payload = {
                ...formData,
                category: { id: formData.category.id }
            };

            if (editingId) {
                await updateFaq(editingId, payload);
            } else {
                await createFaq(payload);
            }
            setIsModalOpen(false);
            fetchFaqs();
        } catch (error) {
            console.error('Failed to save', error);
            alert('저장 실패했습니다.');
        }
    };

    const quillRef = React.useRef<ReactQuill>(null);

    const imageHandler = React.useCallback(() => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        input.onchange = async () => {
            if (input.files && input.files[0]) {
                const file = input.files[0];
                const formData = new FormData();
                formData.append('image', file);

                try {
                    const response = await client.post('/v1/notices/upload-image', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });

                    const url = response.data;

                    const quill = quillRef.current?.getEditor();
                    if (quill) {
                        const range = quill.getSelection(true);
                        quill.insertEmbed(range.index, 'image', url);
                    }
                } catch (error) {
                    console.error('Error uploading image:', error);
                    alert('이미지 업로드 중 오류가 발생했습니다.');
                }
            }
        };
    }, []);

    const modules = React.useMemo(() => ({
        toolbar: {
            container: [
                [{ 'header': [1, 2, false] }],
                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
                ['link', 'image'],
                ['clean']
            ],
            handlers: {
                image: imageHandler
            }
        },
        imageResize: {
            modules: ['Resize', 'DisplaySize']
        }
    }), [imageHandler]);

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike', 'blockquote',
        'list', 'bullet', 'indent',
        'link', 'image'
    ];

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>FAQ 관리</h1>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button className={styles.secondaryBtn} onClick={() => setIsCategoryManagerOpen(true)}>
                        <Settings size={20} />
                        카테고리 관리
                    </button>
                    <button className={styles.primaryBtn} onClick={handleCreate}>
                        <Plus size={20} />
                        FAQ 등록
                    </button>
                </div>
            </div>

            <div className={styles.tableContainer}>
                <div style={{ overflowX: 'auto' }}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>카테고리</th>
                                <th>질문</th>
                                <th style={{ textAlign: 'center' }}>공개</th>
                                <th>작성일</th>
                                <th style={{ textAlign: 'center' }}>관리</th>
                            </tr>
                        </thead>
                        <tbody>
                            {faqs.map((faq) => (
                                <tr key={faq.id}>
                                    <td>{faq.id}</td>
                                    <td>{faq.category?.name || '-'}</td>
                                    <td style={{ textAlign: 'left', fontWeight: 'bold' }}>{faq.question}</td>
                                    <td style={{ textAlign: 'center' }}>
                                        {faq.isVisible ? (
                                            <span style={{ color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                                <Eye size={16} />
                                                공개
                                            </span>
                                        ) : (
                                            <span style={{ color: '#9ca3af', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                                <EyeOff size={16} />
                                                비공개
                                            </span>
                                        )}
                                    </td>
                                    <td>{new Date(faq.createdAt).toLocaleDateString()}</td>
                                    <td style={{ textAlign: 'center' }}>
                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                            <button
                                                onClick={() => handleEdit(faq)}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6' }}
                                            >
                                                <Edit size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(faq.id)}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {faqs.length === 0 && (
                                <tr>
                                    <td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                                        등록된 FAQ가 없습니다.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className={styles.pagination}>
                <button disabled={page === 0} onClick={() => fetchFaqs(page - 1)}>
                    <ChevronLeft size={16} /> 이전
                </button>
                <span>{page + 1} / {totalPages > 0 ? totalPages : 1}</span>
                <button disabled={page >= totalPages - 1} onClick={() => fetchFaqs(page + 1)}>
                    다음 <ChevronRight size={16} />
                </button>
            </div>

            {isModalOpen && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal} style={{ maxWidth: '800px' }}>
                        <h3>{editingId ? 'FAQ 수정' : 'FAQ 등록'}</h3>
                        <form onSubmit={handleSubmit}>
                            <div className={styles.formGroup}>
                                <label>카테고리</label>
                                <select
                                    value={formData.category?.id || ''}
                                    onChange={(e) => {
                                        const selectedId = Number(e.target.value);
                                        const selectedCat = categories.find(c => c.id === selectedId);
                                        if (selectedCat) setFormData({ ...formData, category: selectedCat });
                                    }}
                                >
                                    {categories.map((cat) => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className={styles.formGroup}>
                                <label>질문</label>
                                <input
                                    type="text"
                                    value={formData.question}
                                    onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                                    placeholder="질문을 입력하세요"
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label>답변</label>
                                <div style={{ height: '400px', marginBottom: '50px' }}>
                                    <ReactQuill
                                        ref={quillRef}
                                        theme="snow"
                                        value={formData.answer}
                                        onChange={(answer) => setFormData({ ...formData, answer })}
                                        modules={modules}
                                        formats={formats}
                                        style={{ height: '350px' }}
                                    />
                                </div>
                            </div>

                            <div className={styles.formGroup} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                                <input
                                    type="checkbox"
                                    id="isVisible"
                                    checked={formData.isVisible}
                                    onChange={(e) => setFormData({ ...formData, isVisible: e.target.checked })}
                                    style={{ width: '20px', height: '20px' }}
                                />
                                <label htmlFor="isVisible" style={{ marginBottom: 0, cursor: 'pointer' }}>공개 설정</label>
                            </div>

                            <div className={styles.formActions}>
                                <button type="button" className={styles.secondaryBtn} onClick={() => setIsModalOpen(false)}>
                                    취소
                                </button>
                                <button type="submit" className={styles.primaryBtn}>
                                    저장
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {isCategoryManagerOpen && (
                <FaqCategoryManager onClose={() => {
                    setIsCategoryManagerOpen(false);
                    fetchCategories();
                }} />
            )}
        </div>
    );
}
